import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Upload,
  message
} from 'antd'
import {
  DownloadOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { ordersApi } from 'api/orders'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import {
  EOrderItemsStatus,
  EOrderStatus,
  ERecieveMethod,
  ECdekBarcodeFormat,
  IAdminGetOrderResponse,
  IAdminUpdateOrderRequest,
  IUpdateAdminOrderFormData
} from 'models/orders'
import React from 'react'
import dayjs from 'dayjs'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { MenuInfo } from 'rc-menu/lib/interface'
import { AxiosError } from 'axios'
import classNames from 'classnames'
import { sellerApi } from 'api/seller'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import { RefundFlowModal } from 'components/orders/RefundFlowModal/RefundFlowModal'
import { SuperOrderModal } from 'components/orders/SuperOrder/SuperOrderModal'
import { OrderModalLoader, OrderModalTitle } from 'components/orders/OrderModal/OrderModalParts'
import { showServerError } from 'shared/utils/showServerError'
import { getOrderStatusItems, orderTags } from 'shared/constants/orders'
import { platformColors, platformLabels } from 'shared/constants/platforms'
import {
  sellerNotifyChannelColors,
  sellerNotifyChannelLabels,
  sellerNotifyChannelOrder
} from 'shared/constants/sellerNotifyChannels'
import { IMAGES_URL } from 'shared/constants/urls'
import { IShop } from 'models/seller'
import { ERoles } from 'models/auth'
import { ListInfoItem } from 'components/orders/ListInfoItem/ListInfoItem'
import { OrderStatusHistory } from 'components/orders/OrderStatusHistory/OrderStatusHistory'
import { OrderDeadline } from 'components/orders/OrderDeadline/OrderDeadline'
import { ExtendDeadline } from 'components/orders/ExtendDeadline/ExtendDeadline'
import { ChangeShipmentPointButton, canChangeShipmentPoint } from 'components/orders/ChangeShipmentPoint/ChangeShipmentPoint'
import { DenyItemModal } from 'components/orders/DenyItemModal/DenyItemModal'
import { ShipmentPointPicker } from 'components/orders/ChangeShipmentPoint/ShipmentPointPicker'
import { TransactionsModal } from 'components/orders/TransactionsModal/TransactionsModal'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { showMessage } from 'shared/utils/showMessage'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { superAdminRoutes } from 'shared/constants/routes'
import { acceptOrderModal } from 'shared/utils/orders/acceptOrderModal'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { delay } from 'shared/utils/delay'
import { getDeliveryTypeName } from 'shared/utils/orders/getDeliveryTypeName'
import { getDeliveryTrackingUrl } from 'shared/utils/orders/getDeliveryTrackingUrl'
import { useOrderShipFromAddress } from 'shared/hooks/useOrderShipFromAddress'
import { OrderItemsTable } from './OrderItemsTable'
import { OrderPriceSection } from './OrderPriceSection'
import styles from './OrderEdit.module.scss'
import type { IEmbeddedOrder } from 'components/orders/OrderModal/types'

const getNameWords = (...names: Array<string | null | undefined>) =>
  names
    .join(' ')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/\s+/)
    .filter(Boolean)

interface IOrderEditProps {
  embedded?: IEmbeddedOrder
}

export const OrderEdit: React.FC<IOrderEditProps> = ({ embedded }) => {
  const params = useParams<{ id: string }>()
  const id = embedded?.orderId ?? params.id
  const navigate = useNavigate()
  const location = useLocation()
  const navigationState = location.state as { backPath?: string } | null

  const [form] = Form.useForm()

  const [orderData, setOrderData] =
    React.useState<IAdminGetOrderResponse | null>(null)
  const [isLoaded, setIsLoaded] = React.useState(false)

  const [isSubmitLoading, setIsSubmitLoading] = React.useState(false)
  const [isBarcodeLoading, setIsBarcodeLoading] = React.useState(false)
  const [isCdekRefreshing, setIsCdekRefreshing] = React.useState(false)
  const [isShipmentPointOpen, setIsShipmentPointOpen] = React.useState(false)

  const [sellerShops, setSellerShops] = React.useState<IShop[]>([])

  const [refundModal, setRefundModal] = React.useState<{
    open: boolean
    status: EOrderStatus
  }>({ open: false, status: EOrderStatus.CANCELED })
  const [denyItemId, setDenyItemId] = React.useState<string | null>(null)

  const [openTransactionsModal, setOpenTransactionsModal] =
    React.useState(false)

  // Ссылка из бота модерации о неоплаченном счёте сразу открывает супер-заказ
  const [openSuperOrderModal, setOpenSuperOrderModal] = React.useState(() =>
    new URLSearchParams(location.search).has('super_order')
  )

  const downloadDeliveryLabel = async (format: ECdekBarcodeFormat) => {
    try {
      setIsBarcodeLoading(true)
      const response = await ordersApi.getDeliveryLabel(orderData?.order._id as string, format)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `delivery-label-${orderData?.order.number}-${format}.pdf`
      link.click()
      window.URL.revokeObjectURL(blobUrl)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsBarcodeLoading(false)
    }
  }

  const refreshCdekStatus = async () => {
    try {
      setIsCdekRefreshing(true)
      const { data } = await ordersApi.refreshCdekStatus(orderData?.order._id as string)
      if (data.registrationError) {
        message.warning('СДЭК по-прежнему не принял накладную')
      } else {
        message.success('Данные по доставке обновлены')
      }
      await getOrder()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsCdekRefreshing(false)
    }
  }

  const isHasInvoice: boolean =
    Form.useWatch('invoice', {
      form,
      preserve: true
    }) ?? false

  const deliveryType: ERecieveMethod | undefined = Form.useWatch(
    'deliveryType',
    {
      form,
      preserve: true
    }
  )

  const deliveryPrice: string | undefined = Form.useWatch('deliveryPrice', {
    form,
    preserve: true
  })

  const isPickUpPoint = deliveryType === ERecieveMethod.PICKUP_POINT
  // Везёт сам продавец: перевозчика нет, доставка для покупателя бесплатная
  const isOwnDelivery = deliveryType === ERecieveMethod.OWN_DELIVERY

  React.useEffect(() => {
    if (deliveryPrice) {
      const valueReplacedСommaToDot = deliveryPrice.replace(',', '.')
      const validDeliveryPrice = valueReplacedСommaToDot.replace(/[^0-9.]/g, '')
      form.setFieldValue('deliveryPrice', validDeliveryPrice)
    }
  }, [deliveryPrice])

  React.useEffect(() => {
    getOrder()
  }, [])

  React.useEffect(() => {
    if (orderData) {
      getShops()
    }
  }, [orderData])

  const openRefundModal = React.useCallback((status: EOrderStatus) => {
    setRefundModal({ open: true, status })
  }, [])

  const closeRefundModal = React.useCallback(() => {
    setRefundModal((prev) => ({ ...prev, open: false }))
  }, [])

  const closeTransactionsModal = React.useCallback(() => {
    setOpenTransactionsModal(false)
    bodyScrollLock.disable()
  }, [])

  const onOpenTransactionsModal = React.useCallback(() => {
    setOpenTransactionsModal(true)
    bodyScrollLock.enable()
  }, [])

  const closeSuperOrderModal = () => {
    setOpenSuperOrderModal(false)
    if (location.search) {
      navigate(location.pathname, { replace: true, state: location.state })
    }
  }

  const getShops = async () => {
    try {
      const response = await sellerApi.getShops(
        orderData?.order.seller_id?._id as string
      )
      setSellerShops(response.data)
      form.setFieldValue('shopId', orderData?.order?.shop_id?._id)
    } catch {
      showMessage('error', 'Не удалось получить магазины продавца', '')
    }
  }

  const getOrder = async () => {
    try {
      message.loading('Загрузка данных о заказе')
      const response = await ordersApi.getOrder(id as string)
      setOrderData(response.data)
      setFormData(response.data)
    } catch (e) {
      if (embedded) {
        showServerError(e)
        return embedded.onClose()
      }
      const error = e as AxiosError
      if (
        error.response?.status === 500 ||
        error.response?.status === 400 ||
        error.response?.status === 404
      ) {
        return navigate(superAdminRoutes.ORDERS)
      }
      showServerError(e)
    } finally {
      message.destroy()
      setIsLoaded(true)
    }
  }

  const setFormData = (data: IAdminGetOrderResponse) => {
    form.setFields([
      {
        name: 'deliveryPrice',
        value:
          data.order?.delivery_info?.delivery_sum?.toString() ??
          data.order?.pvz?.delivery_sum?.toString()
      },
      { name: 'trackNumber', value: data.order?.delivery_info?.tracking },
      { name: 'deliveryType', value: data.order.receive_method },
      {
        name: 'deliveryAddress',
        value:
          data.order?.delivery_info?.address?.address ??
          data.order?.pvz?.location?.address_full
      },
      { name: 'note', value: data.order?.note }
    ])
    if (data.order?.preparation_date) {
      form.setFieldValue('prepareDate', dayjs(data.order.preparation_date))
    }
    if (data.order?.delivery_info?.invoice) {
      form.setFieldValue('invoice', [
        {
          uid: data.order.delivery_info.invoice,
          thumbUrl: data.order.delivery_info.invoice,
          name: 'накладная.pdf'
        }
      ])
    } else {
      form.setFieldValue('invoice', undefined)
    }
  }

  const changeStatus = async (status: EOrderStatus, cancelReason?: string, options?: { throwOnError?: boolean }): Promise<boolean> => {
    try {
      message.loading('Обновление статуса')
      await ordersApi.changeStatus({
        status,
        orderId: orderData?.order._id as string,
        reason_of_cancel: cancelReason
      })
      if (status === EOrderStatus.CONFIRMED_BY_SELLER) {
        await delay(3000)
        getOrder()
      } else {
        setOrderData({
          ...(orderData as IAdminGetOrderResponse),
          order: {
            ...(orderData?.order as IAdminGetOrderResponse['order']),
            status
          }
        })
      }
      return true
    } catch (e) {
      // При возврате ошибку показывает сама модалка возврата — пробрасываем её
      if (options?.throwOnError) throw e
      showServerError(e)
      return false
    } finally {
      message.destroy()
    }
  }

  const agreedItems = (orderData?.order.items ?? []).filter(
    (item) => item.items_status === EOrderItemsStatus.AGREED
  )

  // Последнюю единицу последнего товара не убрать — это отмена всего заказа
  const removeProductFromOrder = (itemId: string) => {
    if (agreedItems.length <= 1 && agreedItems.every((item) => item.count <= 1)) {
      return openRefundModal(EOrderStatus.CANCELED)
    }
    setDenyItemId(itemId)
    bodyScrollLock.enable()
  }

  // Ошибку не гасим: модалка ждёт исключения, чтобы показать ответ ЮKassa
  const confirmDenyItem = async (count?: number) => {
    await ordersApi.removeProduct(orderData?.order._id as string, {
      item_id: denyItemId as string,
      count
    })
  }

  const switchToCancelOrder = () => {
    setDenyItemId(null)
    bodyScrollLock.disable()
    openRefundModal(EOrderStatus.CANCELED)
  }

  // Без setFormData, чтобы не сбросить несохранённые правки формы
  const closeDenyItemModal = async () => {
    setDenyItemId(null)
    bodyScrollLock.disable()
    try {
      const response = await ordersApi.getOrder(id as string)
      setOrderData(response.data)
    } catch (e) {
      showServerError(e)
    }
  }

  // Возврат/отмена из выпадающего списка статусов: единая модалка возврата
  const handleRefundConfirm = async () => {
    await changeStatus(refundModal.status, undefined, { throwOnError: true })
  }

  const goBack = () => {
    navigate(navigationState?.backPath ?? superAdminRoutes.ORDERS)
  }

  const onTagClick = (e: MenuInfo, itemId: string) => {
    if (e.key === EOrderStatus.CONFIRMED_BY_SELLER) {
      return acceptOrderModal(async () => {
        await changeStatus(EOrderStatus.CONFIRMED_BY_SELLER)
        bodyScrollLock.disable()
      })
    }

    if (e.key === EOrderStatus.CANCELED) {
      return openRefundModal(EOrderStatus.CANCELED)
    }

    if (e.key === EOrderStatus.RETURNED) {
      return openRefundModal(EOrderStatus.RETURNED)
    }

    changeStatus(e.key as EOrderStatus, itemId)
  }

  const onSubmit = async () => {
    try {
      await form.validateFields()
      const data: IUpdateAdminOrderFormData = form.getFieldsValue()
      setIsSubmitLoading(true)
      const baseFormData: IAdminUpdateOrderRequest = {
        items: orderData?.order.items ?? [],
        delivery_info: {
          receive_method: data.deliveryType,
          // Адрес ПВЗ не редактируется, но бэкенд перезаписывает его присланным — без него сотрётся
          address:
            data.deliveryType === ERecieveMethod.PVZ
              ? orderData?.order.pvz?.location?.address_full
              : data.deliveryAddress,
          preparation_date: data.prepareDate
            ? new Date(
              data.prepareDate
                .hour(23)
                .minute(59)
                .second(59)
                .format('YYYY-MM-DDTHH:mm:ss')
            )
            : undefined,
          shop_id: data.shopId
        },
        note: data.note
      }
      if (
        data.deliveryType !== ERecieveMethod.PICKUP_POINT &&
        data.deliveryType !== ERecieveMethod.OWN_DELIVERY
      ) {
        baseFormData['cdek_info'] = {
          tracking: data.trackNumber,
          delivery_sum: Number(data.deliveryPrice)
        }
      }
      if (data.invoice) {
        baseFormData['invoice'] = data.invoice[0].originFileObj
      }
      const response = await ordersApi.updateOrder(
        orderData?.order?._id as string,
        baseFormData
      )
      setOrderData(response.data)
      setFormData(response.data)
      message.success('Данные успешно обновлены')
    } catch (e: any) {
      if (e.errorFields) {
        return showMessage('error', 'Заполните все поля корректно', '')
      }
      showServerError(e)
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const shipFromAddress = useOrderShipFromAddress(orderData?.order)

  if (!isLoaded || !orderData) {
    return embedded ? <OrderModalLoader /> : null
  }

  const denyItem = agreedItems.find((item) => item._id === denyItemId) ?? null
  // «Заказ не себе» в заказе не отмечается. У заказа себе ФИО получателя копируется из профиля,
  // бывает без отчества, поэтому показываем его, только если в нём есть слова не из профиля
  const recipientName = orderData.order.delivery_info?.full_name?.trim()
  const profileNameWords = getNameWords(
    orderData.order.user_id?.full_name,
    orderData.order.user_id?.surname,
    orderData.order.user_id?.name,
    orderData.order.user_id?.patronymic
  )
  const isRecipientNameShown =
    !!recipientName &&
    getNameWords(recipientName).some((word) => !profileNameWords.includes(word))
  const sellerNotifiedVia = sellerNotifyChannelOrder.filter((channel) =>
    orderData.order.seller_notified_via?.includes(channel)
  )
  const orderStatusTag = orderTags[orderData.order.status] ?? {
    label: orderData.order.status,
    color: 'default'
  }
  const deliveryProvider =
    orderData.order.delivery_info?.provider ?? orderData.order.pvz?.provider
  const isYandexDelivery = deliveryProvider === 'YANDEX'
  const deliveryTitle = getDeliveryTypeName(
    orderData.order.receive_method,
    deliveryProvider,
    orderData.order.pvz?.name
  )
  const deliveryTrackingUrl = getDeliveryTrackingUrl(
    orderData.order.delivery_info,
    deliveryProvider
  )
  const deliveryError =
    orderData.order.delivery_info?.error ??
    orderData.order.delivery_info?.cdek_error

  return (
    <>
      <div style={{ paddingBottom: embedded ? 0 : 20 }}>
        {embedded ? (
          <OrderModalTitle
            orderId={orderData.order._id}
            number={orderData.order.number}
            style={{ marginBottom: 25 }}
          />
        ) : (
          <MainTitle
            isGoBack
            onGoBack={goBack}
            text={`Заказ № ${orderData.order.number}`}
            style={{ marginBottom: 25 }}
          />
        )}
        <div className={styles.subHeader}>
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Статус заказа:
          </Typography.Title>
          <div className={styles.statusWrapper}>
            {orderData.order.status === EOrderStatus.CANCELED ||
              orderData.order.status === EOrderStatus.RETURNED ||
              orderData.order.status === EOrderStatus.NOT_PAID ? (
                <Tag color={orderStatusTag.color}>
                  {orderStatusTag.label}
                </Tag>
              ) : (
                <StatusDropdown
                  items={getOrderStatusItems(
                    orderData.order.status,
                    ERoles.ADMIN,
                    orderData.order.receive_method
                  )}
                  label={orderStatusTag.label}
                  color={orderStatusTag.color}
                  onClick={(e) => onTagClick(e, orderData.order._id)}
                />
              )}
            <OrderDeadline order={orderData.order} />
            <ExtendDeadline order={orderData.order} onExtended={getOrder} />
          </div>
          <div className={styles.headerActions}>
            <Button
              htmlType='button'
              icon={<UnorderedListOutlined />}
              onClick={() => setOpenSuperOrderModal(true)}
            >
              Открыть супер-заказ
            </Button>
            <Button
              htmlType='button'
              loading={isSubmitLoading}
              onClick={onSubmit}
            >
              Сохранить изменения
            </Button>
            {orderData.order.status !== EOrderStatus.CANCELED &&
              orderData.order.status !== EOrderStatus.NOT_PAID && (
              <Button
                danger
                htmlType='button'
                className={styles.cancelButton}
                onClick={() => openRefundModal(EOrderStatus.CANCELED)}
              >
                Отменить заказ
              </Button>
            )}
          </div>
        </div>
        <OrderItemsTable
          items={agreedItems}
          canRemoveProduct={
            orderData.order.status === EOrderStatus.ON_CONFIRMATION &&
            orderData.order.receive_method !== ERecieveMethod.EXPRESS
          }
          isRemoveProductId={denyItemId}
          onRemoveProduct={removeProductFromOrder}
        />
        <Form name='infoForm' form={form} size='small'>
          <div className={styles.detailsContainer}>
            <div className={styles.detailsWrapper} style={{ width: isPickUpPoint ? '66.6666%' : '100%' }}>
              {deliveryType !== ERecieveMethod.PICKUP_POINT && (
                <div
                  className={classNames(
                    styles.detailsItem,
                    styles.columnWithLine,
                    styles.cdekDelivery
                  )}
                >
                  <Typography.Title className={styles.detailsItemTitle} level={3}>
                    {deliveryTitle}
                  </Typography.Title>
                  <ListInfoItem
                    name='Вес заказа'
                    value={`${(orderData.order.weight / 1000).toFixed(1)} кг`}
                    style={{ marginBottom: 15, gap: 15 }}
                    valueStyle={{ flexGrow: 0 }}
                  />
                  {!isOwnDelivery && (
                    <>
                      <Form.Item
                        required={false}
                        name='deliveryPrice'
                        label='Стоимость доставки'
                        rules={[
                          {
                            required: true,
                            message: ''
                          },
                          {
                            whitespace: true,
                            message: ''
                          }
                        ]}
                        style={{ marginBottom: 15 }}
                      >
                        <Input
                          placeholder='Стоимость доставки'
                          className={styles.deliveryPrice}
                          maxLength={7}
                        />
                      </Form.Item>
                      <Form.Item
                        required={false}
                        name='trackNumber'
                        label='Отслеживание'
                        style={{ marginBottom: 15 }}
                      >
                        <Input
                          placeholder='Номер отслеживания'
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item
                        name='invoice'
                        valuePropName='fileList'
                        getValueFromEvent={normalizeFile}
                        label='Накладная'
                      >
                        <Upload
                          accept='.pdf'
                          maxCount={1}
                          customRequest={() => false}
                          beforeUpload={(file) => {
                            const isValid = file.type === 'application/pdf'
                            if (!isValid) {
                              showMessage(
                                'error',
                                'Поддерживаются только формат PDF',
                                ''
                              )
                            } else {
                              message.success('Накладная успешно загружена')
                            }
                            return isValid || Upload.LIST_IGNORE
                          }}
                          itemRender={(_, file) => {
                            return (
                              <div className={styles.invoice}>
                                <PaperClipOutlined
                                  style={{ opacity: 0.45, fontSize: 22 }}
                                />
                                <Typography.Link
                                  disabled={!file.thumbUrl}
                                  href={`${IMAGES_URL}${file.thumbUrl}`}
                                  target='_blank'
                                >
                                  накладная.pdf
                                </Typography.Link>
                              </div>
                            )
                          }}
                        >
                          <Button icon={<UploadOutlined />}>Загрузить файл</Button>
                        </Upload>
                      </Form.Item>
                      {!isYandexDelivery && (
                        <Link to={orderData.order?.delivery_info?.tracking ? `https://lk.cdek.ru/order-history/${orderData.order.delivery_info.tracking}/view` : 'https://lk.cdek.ru/order-history'} target='_blank'>
                          <Button
                            size='middle'
                            htmlType='button'
                            className={styles.deliveryActionButton}
                            style={{ marginTop: isHasInvoice ? 40 : 0 }}
                          >
                            Редактировать доставку
                          </Button>
                        </Link>
                      )}
                    </>
                  )}
                  {deliveryTrackingUrl && (
                    <Button
                      htmlType='button'
                      href={deliveryTrackingUrl}
                      target='_blank'
                      className={styles.deliveryActionButton}
                      style={{ marginTop: 15 }}
                    >
                      Отследить заказ
                    </Button>
                  )}
                  {deliveryError && (
                    <Alert
                      type='error'
                      showIcon
                      style={{ marginTop: 15 }}
                      message={`Ошибка создания доставки ${isYandexDelivery ? 'Яндекс' : 'СДЭК'}`}
                      description={`${deliveryError.replace(/\.*$/, '')}. Обратитесь в поддержку.`}
                      action={!isYandexDelivery ? (
                        <Button
                          size='small'
                          htmlType='button'
                          icon={<ReloadOutlined />}
                          loading={isCdekRefreshing}
                          onClick={refreshCdekStatus}
                        >
                          Обновить
                        </Button>
                      ) : undefined}
                    />
                  )}
                  {isYandexDelivery &&
                    (orderData.order.delivery_info?.external_id ||
                      orderData.order.delivery_info?.label) && (
                    <Button
                      htmlType='button'
                      icon={<DownloadOutlined />}
                      loading={isBarcodeLoading}
                      onClick={() => downloadDeliveryLabel(ECdekBarcodeFormat.A6)}
                      className={styles.deliveryActionButton}
                      style={{ marginTop: 15 }}
                    >
                      Скачать штрихкод
                    </Button>
                  )}
                  {!deliveryError &&
                    orderData.order.status === EOrderStatus.CONFIRMED_BY_SELLER &&
                    orderData.order.delivery_info?.provider !== 'YANDEX' &&
                    (orderData.order.delivery_info?.external_id ||
                      orderData.order.delivery_info?.cdek_uuid) && (
                    <Dropdown
                      menu={{
                        items: Object.values(ECdekBarcodeFormat).map((format) => ({
                          key: format,
                          label: format
                        })),
                        onClick: ({ key }) => downloadDeliveryLabel(key as ECdekBarcodeFormat)
                      }}
                      trigger={['click']}
                    >
                      <Button
                        htmlType='button'
                        icon={<DownloadOutlined />}
                        loading={isBarcodeLoading}
                        className={styles.deliveryActionButton}
                        style={{ marginTop: 15 }}
                      >
                        Скачать штрихкод
                      </Button>
                    </Dropdown>
                  )}
                </div>
              )}
              <div className={classNames(styles.detailsItem, { [styles.shippingInfoWithCdek]: !isPickUpPoint })} style={{ width: isPickUpPoint ? 'calc(100% / 2)' : 'calc(100% / 3)' }}>
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  Информация о доставке
                </Typography.Title>
                <Form.Item
                  required={false}
                  name='deliveryType'
                  label='Способ доставки'
                  rules={[
                    {
                      required: true,
                      message: ''
                    }
                  ]}
                  style={{ marginBottom: 15 }}
                >
                  <Select
                    placeholder='Способ доставки'
                    disabled={deliveryType === ERecieveMethod.EXPRESS}
                  >
                    <Select.Option value={ERecieveMethod.COURIER}>
                      Курьером до двери
                    </Select.Option>
                    <Select.Option value={ERecieveMethod.PVZ}>
                      В пункт выдачи
                    </Select.Option>
                    <Select.Option value={ERecieveMethod.EXPRESS}>
                      Яндекс экспресс доставка
                    </Select.Option>
                    <Select.Option value={ERecieveMethod.PICKUP_POINT}>
                      Самовывоз из магазина
                    </Select.Option>
                    {/* Перевести заказ на неё нельзя: зону и лимиты продавца проверяет оформление */}
                    {orderData.order.receive_method === ERecieveMethod.OWN_DELIVERY && (
                      <Select.Option value={ERecieveMethod.OWN_DELIVERY}>
                        Самостоятельная доставка
                      </Select.Option>
                    )}
                  </Select>
                </Form.Item>
                <Form.Item
                  required={false}
                  name='prepareDate'
                  label='Подготовка до:'
                  style={{ marginBottom: 15 }}
                >
                  <DatePicker
                    format='DD/MM/YYYY'
                    placeholder='Подготовка до'
                    style={{ width: '100%', borderRadius: 4 }}
                    inputReadOnly
                  />
                </Form.Item>
                {deliveryType === ERecieveMethod.PICKUP_POINT ? (
                  <Form.Item
                    required={false}
                    name='shopId'
                    label='Самовывоз из:'
                    rules={[
                      {
                        required: true,
                        message: ''
                      }
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder='Выберите магазин'
                    >
                      {sellerShops.map((el) => (
                        <Select.Option key={el._id} value={el._id}>
                          {el.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : deliveryType === ERecieveMethod.PVZ ? (
                  <ListInfoItem
                    name='Доставка до'
                    value={orderData.order.pvz?.location?.address_full ?? '—'}
                  />
                ) : (
                  <Form.Item
                    required={false}
                    name='deliveryAddress'
                    label='Адрес доставки'
                    rules={[
                      {
                        required: true,
                        message: ''
                      },
                      {
                        whitespace: true,
                        message: ''
                      }
                    ]}
                    style={{ marginBottom: 15 }}
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder='Адрес доставки'
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )}
                {/* Пункт меняется у сохранённого заказа — при несохранённой смене способа доставки он не к месту */}
                {deliveryType === orderData.order.receive_method && shipFromAddress && (
                  <ListInfoItem
                    name='Отправка из'
                    value={
                      <>
                        {shipFromAddress}
                        <ChangeShipmentPointButton
                          order={orderData.order}
                          onClick={() => setIsShipmentPointOpen((prev) => !prev)}
                        />
                      </>
                    }
                  />
                )}
              </div>
              <OrderPriceSection
                order={orderData.order}
                commission={orderData.commission}
                deliveryPrice={deliveryPrice}
                deliveryType={deliveryType}
                isPickUpPoint={isPickUpPoint}
                onOpenTransactionsModal={onOpenTransactionsModal}
              />
            </div>
          </div>
          {isShipmentPointOpen &&
            deliveryType === orderData.order.receive_method &&
            canChangeShipmentPoint(orderData.order) && (
            <div className={styles.detailsContainer}>
              <ShipmentPointPicker
                key={orderData.order._id}
                order={orderData.order}
                currentAddress={shipFromAddress}
                onClose={() => setIsShipmentPointOpen(false)}
                onChanged={getOrder}
              />
            </div>
          )}
          <div className={styles.detailsContainer}>
            <div className={styles.detailsWrapper}>
              <div
                className={classNames(styles.detailsItem, styles.columnWithLine, styles.partiesColumn)}
              >
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  Продавец
                </Typography.Title>
                <div className={styles.sellerShopNameWrapper}>
                  <Typography.Text className={styles.sellerShopNameTitle}>Магазин:</Typography.Text>
                  <Link
                    to={getSellerEditPath(
                      orderData.order.seller_id?._id as string
                    )}
                    className={styles.sellerShopNameLink}
                  >
                    <Typography.Text className={styles.sellerShopName}>
                      {orderData.order.seller_id?.shop_details.shop_name}
                    </Typography.Text>
                  </Link>
                </div>
                <ListInfoItem
                  name='Контактное лицо'
                  value={orderData.order?.seller_id?.user_id?.name as string}
                />
                <ListInfoItem
                  name='Телефон'
                  value={phoneMaskStatic(
                    orderData.order?.seller_id?.user_id?.tel as number
                  )}
                />
                {orderData.order?.seller_id?.user_id?.email && (
                  <ListInfoItem
                    name='Почта'
                    value={orderData.order.seller_id.user_id.email}
                    wrapValue
                  />
                )}
                {orderData.order.seller_notified_via && (
                  <ListInfoItem
                    name='Продавец уведомлен'
                    value={
                      sellerNotifiedVia.length ? (
                        sellerNotifiedVia.map((channel, index) => (
                          <Tag
                            key={channel}
                            color={sellerNotifyChannelColors[channel]}
                            style={index === sellerNotifiedVia.length - 1 ? { marginRight: 0 } : undefined}
                          >
                            {sellerNotifyChannelLabels[channel]}
                          </Tag>
                        ))
                      ) : (
                        'Не уведомлен'
                      )
                    }
                  />
                )}
                <Typography.Title
                  className={classNames(styles.detailsItemTitle, styles.buyerTitle)}
                  level={3}
                >
                  Покупатель
                </Typography.Title>
                {!orderData.order?.user_id && (
                  <Typography.Title level={5} style={{ opacity: 0.5 }}>
                    Аккаунт удален
                  </Typography.Title>
                )}
                {orderData.order?.user_id?._id && (
                  <div className={styles.sellerShopNameWrapper}>
                    <Typography.Text className={styles.sellerShopNameTitle}>Аккаунт:</Typography.Text>
                    <Link
                      to={getUserEditPath(orderData.order.user_id._id)}
                      className={styles.sellerShopNameLink}
                    >
                      <Typography.Text className={styles.sellerShopName}>
                        {orderData.order.user_id.full_name ||
                          orderData.order.user_id.name ||
                          (orderData.order.user_id.tel
                            ? phoneMaskStatic(orderData.order.user_id.tel)
                            : orderData.order.user_id.email) ||
                          'Открыть'}
                      </Typography.Text>
                    </Link>
                  </div>
                )}
                {isRecipientNameShown && (
                  <ListInfoItem name='ФИО' value={recipientName} />
                )}
                {(orderData.order?.delivery_info?.tel ||
                  orderData.order.user_id?.tel) && (
                  <ListInfoItem
                    name='Телефон'
                    value={phoneMaskStatic(
                      orderData.order?.delivery_info?.tel ??
                        (orderData.order.user_id?.tel as number)
                    )}
                  />
                )}
                {orderData.order.user_id?.email && (
                  <ListInfoItem
                    name='Почта'
                    value={orderData.order.user_id.email}
                    wrapValue
                  />
                )}
                <ListInfoItem
                  name='Платформа'
                  value={
                    orderData.platform ? (
                      <Tag color={platformColors[orderData.platform]} style={{ marginRight: 0 }}>
                        {platformLabels[orderData.platform]}
                      </Tag>
                    ) : (
                      'Не определено'
                    )
                  }
                />
              </div>
              <div
                className={classNames(styles.detailsItem, styles.columnWithLine, styles.historyColumn)}
              >
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  История заказа
                </Typography.Title>
                <OrderStatusHistory order={orderData.order} />
              </div>
              <div className={styles.detailsItem}>
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  Заметка менеджера
                </Typography.Title>
                <Form.Item name='note' style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={4} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </div>
      <RefundFlowModal
        open={refundModal.open}
        onConfirm={handleRefundConfirm}
        onClose={closeRefundModal}
      />
      <DenyItemModal
        item={denyItem}
        isLastItem={agreedItems.length === 1}
        onConfirm={confirmDenyItem}
        onCancelOrder={switchToCancelOrder}
        onClose={closeDenyItemModal}
        isSuperAdmin
      />
      <TransactionsModal
        isOpen={openTransactionsModal}
        orderId={orderData.order._id}
        orderStatus={orderData.order.status}
        onClose={closeTransactionsModal}
      />
      <SuperOrderModal
        open={openSuperOrderModal}
        orderId={orderData.order._id}
        onClose={closeSuperOrderModal}
        onOrdersChanged={getOrder}
      />
    </>
  )
}
